import type { GridApi, GridOptions, GridReadyEvent, RowClassParams, SelectionChangedEvent } from "ag-grid-community"
import { AgGridReact } from "ag-grid-react"
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef } from "react"

import { GRID_DEFAULT_OPTIONS, PRICING_GRID_THEME, getPricingEditColumns } from "./gridConfigs"
import { type PricingGridRow } from "./types"

import { type PriceChangeType } from "@/graphql/codegen/graphql"
import { useStopGridEditingOnOutsideMouseDown } from "@/hooks/useStopGridEditingOnOutsideMouseDown"
import { SurchargeFormDialog } from "@/screens/Pricing/components/dialogs/SurchargeFormDialog/SurchargeFormDialog"
import type { PriceChangeTableVariant } from "@/screens/Pricing/constants"
import type { PricingEditContext } from "@/screens/Pricing/hooks/usePricingTiersActions"
import { usePricingTiersActions } from "@/screens/Pricing/hooks/usePricingTiersActions"
import type { WarehouseHeaderFormValues } from "@/screens/Pricing/schema/WarehouseHeaderSchema"
import { usePriceTableEditsStore } from "@/screens/Pricing/stores/usePriceTableEditsStore"
import { getPriceChangeEditKey, isPricingRowModified } from "@/screens/Pricing/utils/pricingRow"

export interface PricingTiersTableHandle {
  getSubmitRowData: () => PricingGridRow[]
  getUpdateRowData: () => PricingGridRow[]
  applyPriceChange: (formValues: WarehouseHeaderFormValues) => void
  applyGroupCostChange: (formValues: WarehouseHeaderFormValues) => void
  resetInvalidDates: () => void
  reseed: (rows: PricingGridRow[]) => void
  hasManualEditsInSelection: () => boolean
  hasAnyManualEdits: () => boolean
  flushPendingEdits: () => Promise<void>
}

interface PricingTiersTableProps {
  uomName: string
  initialRows: PricingGridRow[]
  isUomChanged?: boolean
  isQuoteSource?: boolean
  onSelectionChange?: (info: { selected: number; total: number }) => void
  onChangesChange?: (hasChanges: boolean) => void
  readonly?: boolean
  variant: PriceChangeTableVariant
  priceChangeType: PriceChangeType
  editContext?: PricingEditContext
  hasBulkPreview?: boolean
}

export const PricingTiersTable = forwardRef<PricingTiersTableHandle, PricingTiersTableProps>(
  (
    {
      uomName,
      initialRows,
      isUomChanged = false,
      isQuoteSource,
      onSelectionChange,
      onChangesChange,
      readonly,
      variant,
      priceChangeType,
      editContext,
      hasBulkPreview,
    },
    ref
  ) => {
    const gridApiRef = useRef<GridApi<PricingGridRow>>()
    const gridContainerRef = useRef<HTMLDivElement>(null)
    const {
      rowData,
      rowDataRef,
      modifiedRowsRef,
      editingSurchargeRowIndex,
      hasChanges,
      handleGridReady,
      handleCellValueChanged,
      handleAddPriceTier,
      handleClearPriceTier,
      handleEditSurcharge,
      handleSurchargeDialogClose,
      handleSurchargeSubmit,
      isRowNew,
      getAllChangedRowData,
      handleApplyPriceChange,
      handleApplyGroupCostChange,
      resetInvalidDates,
      reseed,
      hasManualEditsInSelection,
      hasAnyManualEdits,
      syncUomRows,
      flushPendingEdits,
    } = usePricingTiersActions(initialRows, variant, editContext)

    useStopGridEditingOnOutsideMouseDown(gridApiRef, gridContainerRef)

    useImperativeHandle(ref, () => ({
      getSubmitRowData: () => getAllChangedRowData(isUomChanged),
      getUpdateRowData: () => rowDataRef.current,
      applyPriceChange: handleApplyPriceChange,
      applyGroupCostChange: handleApplyGroupCostChange,
      resetInvalidDates,
      reseed,
      hasManualEditsInSelection,
      hasAnyManualEdits,
      flushPendingEdits,
    }))

    useEffect(() => {
      onChangesChange?.(hasChanges || isUomChanged)
    }, [hasChanges, isUomChanged, onChangesChange])

    useEffect(() => {
      gridApiRef.current?.redrawRows()
    }, [isUomChanged])

    // Re-run on the actual UoM id too: switching between two non-initial UoMs keeps
    // isUomChanged true, but the persisted uomId must still refresh to match submit.
    useEffect(() => {
      syncUomRows(isUomChanged)
    }, [isUomChanged, editContext?.uomId, syncUomRows])

    const handleGridReadyWithApi = useCallback(
      (event: GridReadyEvent<PricingGridRow>) => {
        gridApiRef.current = event.api
        handleGridReady(event)
        if (editContext) {
          const { selectedRowKeys } = usePriceTableEditsStore.getState()
          event.api.forEachNode((node) => {
            if (node.data && selectedRowKeys[getPriceChangeEditKey(editContext.group.id, node.data)]) {
              node.setSelected(true)
            }
          })
        }
      },
      [handleGridReady, editContext]
    )

    const { groupIndexById, totalGroups } = useMemo(() => {
      const indexById = new Map<string, number>()
      let nextIndex = 0
      for (const row of rowData) {
        const id = row.item.priceChangeItemId
        if (!indexById.has(id)) {
          indexById.set(id, nextIndex++)
        }
      }
      return { groupIndexById: indexById, totalGroups: nextIndex }
    }, [rowData])

    const handleSelectionChanged = useCallback(
      (event: SelectionChangedEvent<PricingGridRow>) => {
        if (editContext) {
          const selectedKeys: string[] = []
          const unselectedKeys: string[] = []
          event.api.forEachNode((node) => {
            if (!node.data) return
            const key = getPriceChangeEditKey(editContext.group.id, node.data)
            if (node.isSelected()) selectedKeys.push(key)
            else unselectedKeys.push(key)
          })
          const { setRowsSelected } = usePriceTableEditsStore.getState()
          setRowsSelected(selectedKeys, true)
          setRowsSelected(unselectedKeys, false)
        }
        if (!onSelectionChange) return
        const selectedIds = new Set(
          event.api
            .getSelectedNodes()
            .map((node) => node.data?.item.priceChangeItemId)
            .filter(Boolean)
        )
        onSelectionChange({ selected: selectedIds.size, total: totalGroups })
      },
      [onSelectionChange, totalGroups, editContext]
    )

    const groupId = editContext?.group.id
    const isRowDefaulted = useCallback(
      (row: PricingGridRow) =>
        groupId !== undefined &&
        Boolean(usePriceTableEditsStore.getState().defaultedRowKeys[getPriceChangeEditKey(groupId, row)]),
      [groupId]
    )
    const defaultedRowKeys = usePriceTableEditsStore((state) => state.defaultedRowKeys)
    useEffect(() => {
      gridApiRef.current?.refreshCells({ columns: ["effectiveDate"], force: true })
    }, [defaultedRowKeys])

    const columnDefs = useMemo(
      () =>
        getPricingEditColumns({
          uomName,
          onAddPriceTier: handleAddPriceTier,
          onClearPriceTier: handleClearPriceTier,
          onEditSurcharge: handleEditSurcharge,
          isRowNew,
          isRowDefaulted,
          isQuoteSource,
          variant,
          getGroupIndex: (id) => groupIndexById.get(id) ?? 0,
          readonly,
          priceChangeType,
          modifiedRows: modifiedRowsRef.current,
          isUomChanged,
        }),
      [
        uomName,
        handleAddPriceTier,
        handleClearPriceTier,
        handleEditSurcharge,
        isRowNew,
        isRowDefaulted,
        variant,
        readonly,
        priceChangeType,
        modifiedRowsRef,
        isUomChanged,
        isQuoteSource,
        groupIndexById,
      ]
    )

    const isRowModified = ({ data }: RowClassParams<PricingGridRow, any>) =>
      Boolean(data && isPricingRowModified({ isUomChanged, modifiedRows: modifiedRowsRef.current, row: data }))

    const gridOptions: GridOptions<PricingGridRow> = {
      rowData,
      columnDefs,
      ...GRID_DEFAULT_OPTIONS,
      rowSelection: {
        mode: "multiRow",
        checkboxes: !readonly,
        headerCheckbox: false,
      },
      rowClassRules: {
        "font-bold": (params) => Boolean(hasBulkPreview) || isRowModified(params),
        "pricing-row-modified": isRowModified,
        "!bg-gray-100": ({ data }) => {
          if (!data) return false
          const groupIdx = groupIndexById.get(data.item.priceChangeItemId) ?? 0
          return groupIdx % 2 === 1
        },
      },
    }

    const editingRow = editingSurchargeRowIndex !== null ? rowData[editingSurchargeRowIndex] : undefined

    return (
      <>
        <div ref={gridContainerRef}>
          <AgGridReact
            {...gridOptions}
            theme={PRICING_GRID_THEME}
            onCellValueChanged={handleCellValueChanged}
            onGridReady={handleGridReadyWithApi}
            onSelectionChanged={handleSelectionChanged}
          />
        </div>
        {editingRow && (
          <SurchargeFormDialog row={editingRow} onSubmit={handleSurchargeSubmit} onClose={handleSurchargeDialogClose} />
        )}
      </>
    )
  }
)

PricingTiersTable.displayName = "PricingTiersTable"
